// The placement drawer: market rows, quotes, binding, and the slip.
//
// Market Responses is the working tab. A placement IS its market panel —
// everything else on the screen is a summary of it.

import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  C, Pill, Panel, Row, MiniTable, NextAction, money0, date, humanise,
} from './kit';
import {
  useAction, Notice, ActionButton, ActionRow, ConfirmAction, RowEditor, Input, FieldRow,
  EditPanel, StatusAction, RemoveAction,
} from './actions';
import { ActivityTab } from './tabs';

// Where a market has got to. `pending` is a row on the slip nobody has sent
// out yet, which is why it is not the same as `approached`.
const MARKET_STATUSES = [
  { value: 'pending', label: 'Not approached' },
  { value: 'approached', label: 'Approached' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'firm_order', label: 'Firm order' },
  { value: 'signed', label: 'Signed' },
  { value: 'declined', label: 'Declined' },
];

export default function PlacementDrawer({ row: r, tab, reload }) {
  const history = useHistory();
  const act = useAction(reload);
  const [markets, setMarkets] = useState(r.reinsurers || []);
  const [layers, setLayers] = useState(r.layers || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setMarkets(r.reinsurers || []); setLayers(r.layers || []); }, [r._id]);

  const bound = r.placementStatus === 'bound';
  const signedPct = markets
    .filter(m => ['signed', 'written'].includes(m.status))
    .reduce((a, m) => a + (Number(m.signedShare) || 0), 0);

  const saveMarkets = (rows) => act.run({
    method: 'patch',
    url: `/reinsurance/placements/${r._id}`,
    body: {
      reinsurers: rows
        .filter(m => String(m.name || '').trim())
        .map(m => ({
          name: String(m.name).trim(),
          status: m.status || 'pending',
          provisionalShare: Number(m.provisionalShare) || 0,
          quotedShare: Number(m.quotedShare) || 0,
          signedShare: Number(m.signedShare) || 0,
          lineSize: Number(m.lineSize) || undefined,
          signedAmount: ['signed', 'written'].includes(m.status)
            ? Math.round((Number(r.capacityRequired) || 0) * ((Number(m.signedShare) || 0) / 100))
            : undefined,
          comments: m.comments || '',
          currency: r.currency,
        })),
    },
    success: 'Market panel saved.',
  });

  // ─── Market Responses ───
  if (tab === 'Market Responses') {
    return (
      <Panel title='Market responses'>
        <Notice error={act.error} note={act.note} />
        <RowEditor
          columns={[
            { key: 'name', label: 'Reinsurer', placeholder: 'Swiss Re' },
            { key: 'status', label: 'Status', options: MARKET_STATUSES, wide: true },
            { key: 'quotedShare', label: 'Quoted %', numeric: true },
            { key: 'signedShare', label: 'Signed %', numeric: true },
            { key: 'comments', label: 'Comments', placeholder: 'Supportive', wide: true },
          ]}
          rows={markets}
          onChange={setMarkets}
          blank={{ name: '', status: 'approached', quotedShare: '', signedShare: '', comments: '' }}
          addLabel='+ Add market'
          saving={act.saving}
          total={{
            label: 'Signed capacity',
            value: `${Math.round(signedPct * 100) / 100}% · ${money0(
              (Number(r.capacityRequired) || 0) * (signedPct / 100), r.currency,
            )}`,
            ok: signedPct <= 100,
            warning: signedPct > 100 ? 'Over-placed — sign down before binding.' : null,
          }}
          footer={(
            <div style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.5 }}>
              A signed line is worked out against the capacity required, so the amount
              follows the percentage. Set a status of Signed for it to count.
            </div>
          )}
          onSave={() => saveMarkets(markets)}
        />
      </Panel>
    );
  }

  // ─── Structure ───
  if (tab === 'Structure') {
    return (
      <Panel title='Layer structure'>
        <Notice error={act.error} note={act.note} />
        <RowEditor
          columns={[
            { key: 'name', label: 'Layer', placeholder: 'Layer 1' },
            { key: 'attachment', label: 'Attachment', numeric: true },
            { key: 'limit', label: 'Limit', numeric: true },
          ]}
          rows={layers}
          onChange={setLayers}
          blank={{ name: `Layer ${layers.length + 1}`, attachment: '', limit: '' }}
          addLabel='+ Add layer'
          saving={act.saving}
          total={{
            label: 'Total limit',
            value: money0(layers.reduce((a, l) => a + (Number(l.limit) || 0), 0), r.currency),
            ok: true,
          }}
          onSave={() => act.run({
            method: 'patch',
            url: `/reinsurance/placements/${r._id}`,
            body: {
              layers: layers.map(l => ({
                name: l.name || '',
                attachment: Number(l.attachment) || 0,
                limit: Number(l.limit) || 0,
                currency: r.currency,
              })),
            },
            success: 'Layer structure saved.',
          })}
        />
      </Panel>
    );
  }

  // ─── Activity ───
  if (tab === 'Activity') return <ActivityTab resource='placements' id={r._id} />;


  // ─── Overview ───
  return (
    <>
      <Notice error={act.error} note={act.note} />

      <Panel title='Placement information'>
        <Row label='Placement ref' value={r.reference} />
        <Row label='Cedant' value={r.cedantName} />
        <Row label='Type' value={humanise(r.dealType)} />
        <Row label='Class' value={r.lineOfBusiness} />
        <Row label='Layer' value={r.layerLabel || r.structureType} />
        <Row label='Territory' value={r.territory || r.geography} />
        <Row label='Target close date' value={date(r.targetCloseDate)} />
        <Row label='Owner' value={r.ownerName} />
        <Row label='Status' value={<Pill status={r.placementStatus} size='sm' />} />
      </Panel>

      <Panel title='Capacity'>
        <Row label='Required' value={money0(r.capacityRequired, r.currency)} />
        <Row
          label='Secured'
          value={r.capacitySecured === null
            ? 'No line signed yet'
            : money0(r.capacitySecured, r.currency)}
          strong
        />
        <Row label='Quoted ROL' value={r.quotedRol ? `${r.quotedRol}%` : null} />
      </Panel>

      <Panel title={`Market responses (${(r.reinsurers || []).length})`}>
        <MiniTable
          head={['Reinsurer', 'Status', 'Signed %']}
          rows={(r.reinsurers || []).map(m => [
            m.name,
            <Pill key={`${m.name}-s`} status={m.status} size='sm' />,
            m.signedShare ? `${m.signedShare}%` : '—',
          ])}
          empty='No market has been approached yet — add them on the Market Responses tab.'
        />
      </Panel>

      {/* ── Move it along ──
          The status a placement is AT, changed here rather than on a form:
          this is the single most-used control on the screen. */}
      <Panel title='Progress'>
        {bound ? (
          // Nothing to choose: a bound placement moves on by closing, not by
          // going back into the market. Rendering the select here would show
          // "Early Discussions", because `bound` is not one of its options.
          <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55 }}>
            This placement is bound. Its signed lines are now closings — work them
            under Closings.
          </div>
        ) : (
        <FieldRow>
          <Input
            label='Placement status'
            value={r.placementStatus || 'early_discussions'}
            onChange={(v) => act.run({
              method: 'patch',
              url: `/reinsurance/placements/${r._id}`,
              body: { placementStatus: v },
              success: `Moved to ${humanise(v)}.`,
            })}
            options={[
              { value: 'early_discussions', label: 'Early Discussions' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'quotes_received', label: 'Quotes Received' },
              { value: 'under_review', label: 'Under Review' },
              { value: 'declined', label: 'Declined' },
            ]}
          />
        </FieldRow>
        )}
        {!bound && (
          <div style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.5, marginTop: 8 }}>
            Bound is not in this list on purpose — a placement becomes bound by
            signing lines, which is what the button below does.
          </div>
        )}
      </Panel>

      {/* ── The record itself ──
          Correcting, moving and withdrawing a record, in one place on the
          Overview tab. Before this a screen could create a record and then
          never touch it again. Removal is a void or a cancel, never a
          delete — see reinsuranceShared.js. */}
      <Panel title='Record'>
        <div style={{ display: 'grid', gap: 10 }}>
          <EditPanel
            record={r}
            endpoint='/reinsurance/placements'
            onSaved={reload}
            fields={[
              { key: 'cedantName', label: 'Cedant' },
              { key: 'lineOfBusiness', label: 'Line of business' },
              { key: 'territory', label: 'Territory' },
              { key: 'ownerName', label: 'Owner' },
              { key: 'targetCloseDate', label: 'Target close', type: 'date' },
            ]}
          />
          {/* `placementStatus`, not `status` — see PLACEMENT_WRITABLE. */}
          <StatusAction
            record={r}
            endpoint='/reinsurance/placements'
            field='placementStatus'
            onSaved={reload}
            options={[{ value: 'early_discussions', label: 'Early Discussions' }, { value: 'in_progress', label: 'In Progress' }, { value: 'quotes_received', label: 'Quotes Received' }, { value: 'under_review', label: 'Under Review' }, { value: 'bound', label: 'Bound' }, { value: 'declined', label: 'Declined' }]}
          />
          <RemoveAction record={r} endpoint='/reinsurance/placements' noun='placement' onRemoved={reload} />
        </div>
      </Panel>

      <Panel title='Actions'>
        <ActionRow>
          {!bound && (
            <ConfirmAction
              label='Bind placement'
              question={`Bind ${r.reference} with ${Math.round(signedPct)}% signed? This fixes the risk and its market lines.`}
              confirmLabel='Bind'
              busy={act.saving}
              onConfirm={() => act.run({
                method: 'patch',
                url: `/reinsurance/placements/${r._id}`,
                body: { placementStatus: 'bound' },
                success: 'Placement bound. It will now appear under Closings.',
              })}
            />
          )}
          <ActionButton
            tone='quiet'
            busy={act.saving}
            onClick={() => act.run({
              url: `/reinsurance/deals/${r._id}/closings/slip`,
              success: 'Slip generated — it is on the deal record.',
            })}
          >
            Generate slip
          </ActionButton>
          <ActionButton tone='quiet' onClick={() => history.push(`/reinsurance/deals/${r._id}/placement`)}>
            Open board
          </ActionButton>
        </ActionRow>
      </Panel>

      {r.nextAction?.label && (
        <NextAction label={r.nextAction.label} detail={r.nextAction.detail} />
      )}
    </>
  );
}
